import { useIsFocused } from "expo-router/react-navigation";
import { router } from 'expo-router';
import { useEffect, useLayoutEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';

import { useOnBoardingSecular } from '@/api/hooks/meHooks';
import { useNineNintyUpload } from '@/api/hooks/nonProfitOrgHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import CheckboxInputField from '@/components/inputs/CheckboxInputField';
import FileInputField from '@/components/inputs/FileInputField';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import { NineNintyData, nineNintyResolver } from '@/lib/validations/NpoOnboardingSchema';
import { containsSection } from '@/utils/containsSection';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

const NineNintyForm = () => {
  const { state: authState } = useAuth();
  const { state: npoScreensState, resetCurrentPage, setInitialNpoData } = useNpoLoginScreenState();
  const { mutateAsync: onBoardingUpdate, isPending: onBoardingIsPending } = useOnBoardingSecular();
  const { mutateAsync: nineNintyUpload, isPending: nineNintyUploadIsPending } = useNineNintyUpload();

  const type = authState.signUpData?.loginEmail?.type;

  const isFocused = useIsFocused();
  useLayoutEffect(() => {
    // Only Secular
    if (isFocused) type === 'CHARITIES' && resetCurrentPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoScreensState.initialNpoData ?? '');
  const initialData = {
    nineNintyForm:
      typeof nonProfitOrg?.nineNintyForm === 'string' ? safeJsonParseArray(nonProfitOrg?.nineNintyForm)[0] : undefined
  };

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    trigger,
    resetField,
    formState: { isValid, isDirty, submitCount }
  } = useForm<NineNintyData>({
    defaultValues: initialData,
    mode: 'onSubmit',
    resolver: nineNintyResolver
  });

  const nineNintyWatch = watch('nineNintyForm');
  const newOrgCheckWatch = watch('newOrgCheck');

  useEffect(() => {
    if (!isDirty) return;
    const nineNintyEmpty = !nineNintyWatch || nineNintyWatch === '' || nineNintyWatch === null;
    if (newOrgCheckWatch) {
      clearErrors('nineNintyForm');
      if (nineNintyEmpty) setValue('nineNintyForm', '');
      trigger();
    } else if (nineNintyEmpty && !initialData.nineNintyForm) {
      resetField('nineNintyForm');
      setError('nineNintyForm', {
        type: 'required',
        message: i18n.t('sign_up.nonProfit.wizard.errors.nineNintyRequested')
      });
    }
  }, [
    clearErrors,
    initialData.nineNintyForm,
    isDirty,
    newOrgCheckWatch,
    nineNintyWatch,
    resetField,
    setError,
    setValue,
    trigger
  ]);

  const onSubmit: SubmitHandler<NineNintyData> = async (data) => {
    try {
      if (data.nineNintyForm && !containsSection(data.nineNintyForm as string, 'nineNintyForm')) {
        nineNintyUpload(
          {
            files: Platform.OS === 'web' ? (data.nineNintyForm as File) : (data.nineNintyForm as string),
            nonProfitOrgId: authState.signUpData?.loginEmail?.npoId ?? ''
          },
          {
            onSuccess: (ocrData) => {
              const newNpoData = {
                id: ocrData.npoId,
                revenue: ocrData?.doc990Data?.revenue,
                totalExpenses: ocrData?.doc990Data?.totalExpenses,
                totalEmployees: ocrData?.doc990Data?.totalEmployees,
                totalEmployeeBeneficits: ocrData?.doc990Data?.totalEmployeeBeneficits,
                totalVolunteers: ocrData?.doc990Data?.totalVolunteers,
                totalSalaries: ocrData?.doc990Data?.totalSalaries,
                totalLandBuildingEquiment: ocrData?.doc990Data?.totalLandBuildingEquiment,
                totalFundraisingMarketing: ocrData?.doc990Data?.totalFundraisingMarketing,
                nineNintyForm: ocrData?.doc990Data?.nineNintyForm,
                totalGrantsPrograms: ocrData?.doc990Data?.totalGrantsPrograms,
                usersBoardDirectory: ocrData.boardDirectories
              };

              setInitialNpoData(
                JSON.stringify({
                  ...nonProfitOrg,
                  ...newNpoData
                })
              );

              onBoardingUpdate(
                {
                  preferenceId: authState.preferenceId ?? '',
                  nineNintyForm: true
                },
                {
                  onSuccess: (_) => {
                    router.navigate('/(app)/login/nonProfit/onBoarding');
                  }
                }
              );
            }
          }
        );
      } else {
        onBoardingUpdate(
          {
            preferenceId: authState.preferenceId ?? '',
            nineNintyForm: true
          },
          {
            onSuccess: (_) => {
              router.navigate('/(app)/login/nonProfit/onBoarding');
            }
          }
        );
      }
    } catch (e: any) {
      console.error(e);
    }
  };

  return (
    <FormWrapper scrollEnable={false}>
      <View style={styles.container}>
        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.nine_ninty.upload_form')}
          infoButtonLabel={i18n.t('sign_in.nonProfit.nine_ninty.upload_form_info')}>
          <FileInputField
            control={control}
            name="nineNintyForm"
            indeterminateProgress={nineNintyUploadIsPending}
            getBlob
          />
          <Text style={{ color: 'gray', fontStyle: 'italic' }}>
            {i18n.t('sign_in.nonProfit.nine_ninty.recommendation')}
          </Text>

          <CheckboxInputField
            name="newOrgCheck"
            control={control}
            label={i18n.t('sign_in.nonProfit.nine_ninty.newOrgCheck_label')}
          />
        </OnboardingSection>

        <View style={styles.buttonsContainer}>
          {nineNintyUploadIsPending && <Text>{i18n.t('mix.large_process_wait')}</Text>}

          <BrandedButton
            loading={onBoardingIsPending || nineNintyUploadIsPending}
            disabled={!isValid && submitCount > 0}
            onPress={handleSubmit(onSubmit)}>
            {i18n.t('mix.next')}
          </BrandedButton>
          <SaveAndContinueLaterButton disabled={onBoardingIsPending || nineNintyUploadIsPending} />
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  title: {
    fontSize: 22,
    marginBottom: 25
  },

  buttonsContainer: {
    flexDirection: 'column',
    gap: 10
  }
});

export default NineNintyForm;
